/**
 * Normalize Food Safety manager report email list (multi-recipient).
 * Mirrors Express foodSafetyManagerEmails.
 */

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
export const FOOD_SAFETY_MANAGER_REPORT_EMAIL_MAX = 10

export const normalizeFoodSafetyManagerReportEmails = raw => {
  let parts = []
  if (Array.isArray(raw)) {
    parts = raw
  } else if (typeof raw === 'string') {
    parts = raw.split(/[,;\n]+/)
  } else if (raw != null) {
    parts = [raw]
  }

  const seen = new Set()
  const out = []
  for (const part of parts) {
    const email = String(part || '')
      .trim()
      .toLowerCase()
    if (!email || seen.has(email)) continue
    if (!EMAIL_RE.test(email)) continue
    seen.add(email)
    out.push(email)
    if (out.length >= FOOD_SAFETY_MANAGER_REPORT_EMAIL_MAX) break
  }
  return out
}

export const resolveFoodSafetyManagerReportEmails = settings => {
  if (!settings) return []
  const fromArray = normalizeFoodSafetyManagerReportEmails(
    settings.managerReportEmails,
  )
  if (fromArray.length) return fromArray
  return normalizeFoodSafetyManagerReportEmails(settings.managerReportEmail)
}

export const isFoodSafetyManagerReportEmail = value =>
  EMAIL_RE.test(
    String(value || '')
      .trim()
      .toLowerCase(),
  )

/** True when the chip list differs from last-saved settings. */
export function foodSafetyManagerEmailsAreDirty(drafts, saved) {
  const d = Array.isArray(drafts) ? drafts : []
  const s = Array.isArray(saved) ? saved : []
  return d.length !== s.length || d.some((email, i) => email !== s[i])
}

/**
 * Merge a still-typed (unchipped) address into the draft list for Save.
 * ReactMultiEmail only chips on Enter/blur — managers often hit Save first (H9 / 29.25).
 */
export function mergeFoodSafetyManagerEmailPending(drafts, pendingTyped) {
  const base = Array.isArray(drafts) ? drafts : []
  const typed = String(pendingTyped || '').trim()
  if (!typed) return { list: base, pending: 'empty' }
  if (!isFoodSafetyManagerReportEmail(typed)) {
    return { list: base, pending: 'invalid' }
  }
  return {
    list: normalizeFoodSafetyManagerReportEmails([...base, typed]),
    pending: 'merged',
  }
}

/** Enable Save when chips are dirty or the pending typed address is a valid email. */
export function canSaveFoodSafetyManagerEmails(drafts, saved, pendingTyped) {
  if (foodSafetyManagerEmailsAreDirty(drafts, saved)) return true
  return isFoodSafetyManagerReportEmail(String(pendingTyped || '').trim())
}

/**
 * Preflight before emailing a Food Safety PDF (29.49).
 * Returns null when recipients exist; otherwise copy for Alert / message.
 */
export function foodSafetyReportsEmailPreflight(
  settingsOrEmails,
  { canManage = false } = {},
) {
  const emails = Array.isArray(settingsOrEmails)
    ? normalizeFoodSafetyManagerReportEmails(settingsOrEmails)
    : resolveFoodSafetyManagerReportEmails(settingsOrEmails)
  if (emails.length) return null
  return {
    title: 'Add a report email',
    message: canManage
      ? 'Add a manager report email in Food Safety setup, then try again.'
      : 'Ask a manager to add a report email in Food Safety setup.',
  }
}
